import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Typography with the serif decision made per level rather than globally.
 *
 * globals.css still applies Literata to every h1/h2/h3, which fires inside cards
 * and forum posts where a serif reads as a mistake, and cannot be opted out of
 * without adding `font-sans` by hand. Levels here are named by role, so a card
 * title can be an <h3> for document structure while looking like a card title.
 *
 * `level` and `as` are separate on purpose: several E2E specs locate content with
 * getByRole("heading", { name }), so the element must stay semantic even when the
 * visual treatment changes.
 */
const heading = cva("text-balance text-[var(--text-heading)]", {
  variants: {
    level: {
      display: "font-serif text-4xl leading-tight tracking-tight sm:text-5xl",
      page: "font-serif text-3xl leading-tight tracking-tight sm:text-4xl",
      section: "font-serif text-2xl leading-snug tracking-tight",
      card: "font-sans text-lg font-semibold leading-snug",
      /** The letterspaced uppercase eyebrow — the third type register. */
      eyebrow:
        "font-sans text-xs font-semibold uppercase tracking-[0.18em] text-[var(--accent-ink)]",
    },
  },
  defaultVariants: { level: "section" },
});

type HeadingProps = React.ComponentPropsWithoutRef<"h2"> &
  VariantProps<typeof heading> & {
    as?: "h1" | "h2" | "h3" | "h4" | "p" | "span";
  };

export function Heading({ as = "h2", level, className, ...props }: HeadingProps) {
  const Tag = as;
  return <Tag className={cn(heading({ level }), className)} {...props} />;
}

const text = cva("", {
  variants: {
    tone: {
      primary: "text-[var(--text-primary)]",
      secondary: "text-[var(--text-secondary)]",
      muted: "text-[var(--text-muted)]",
    },
    size: {
      xs: "text-xs leading-5",
      sm: "text-sm leading-[1.375rem]",
      base: "text-base leading-relaxed",
      lg: "text-lg leading-relaxed",
    },
  },
  defaultVariants: { tone: "primary", size: "sm" },
});

type TextProps = React.ComponentPropsWithoutRef<"p"> &
  VariantProps<typeof text> & { as?: "p" | "span" | "div" };

export function Text({ as = "p", tone, size, className, ...props }: TextProps) {
  const Tag = as;
  return <Tag className={cn(text({ tone, size }), className)} {...props} />;
}
